import { useQuery } from "@tanstack/react-query";
import { Outlet, createFileRoute, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { canRegister } from "../../components/accreditation/model";
import { usePageBar } from "../../components/shell/page-bar";
import { EMPTY_FORM, type RegisterForm, readLocalDraft } from "../../components/accreditation/register/model";
import { RegisterFlow } from "../../components/accreditation/register/register-flow";
import { HOST_COUNTRY_ISO, NOC_TYPE_CODE } from "../../components/accreditation/register/use-register-refs";
import { gamesWithDatesOptions, liveGames } from "../../lib/accreditation/register-options";
import { countryOptionsOptions } from "../../lib/countries/queries";
import { responsibleOrganizationOptionsOptions } from "../../lib/responsible-organizations/queries";

/**
 * Register New Record. `/accreditation/add` is the new-person flow (Zone and
 * Personal, kept on this device until the Personal checkpoint creates the
 * record); `/accreditation/add/$id` resumes or edits an existing record.
 * `games_id` / `responsible_organization_id` preset the Zone ("Register
 * another" keeps the organisation); `fresh` ignores a saved draft; `stage` is the step (Zone / Personal).
 */
export const Route = createFileRoute("/_app/accreditation/add")({
	validateSearch: z.object({
		games_id: z.string().uuid().optional().catch(undefined),
		responsible_organization_id: z.string().uuid().optional().catch(undefined),
		fresh: z.boolean().optional().catch(undefined),
		stage: z.enum(["zone", "personal"]).optional().catch(undefined),
	}),
	component: AddShell,
});

function AddShell() {
	const params = useParams({ strict: false });
	return params.id !== undefined ? <Outlet /> : <NewRecordPage />;
}

function NewRecordPage() {
	const { t } = useTranslation("accreditation");
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const games = useQuery(gamesWithDatesOptions());
	const orgs = useQuery(responsibleOrganizationOptionsOptions());
	const countries = useQuery(countryOptionsOptions());
	// Her bar title; the subtitle stays the nav section ("Accreditation").
	usePageBar({ title: t("register.title") });

	// A login that switches NOC from the role menu stays on this page: the new group starts over.
	const groupId = session.group?.user_group_id ?? null;
	const groupSeen = useRef(groupId);
	// Until the replace below lands, the URL still holds the old NOC's step.
	const switched = groupSeen.current !== groupId;
	useEffect(() => {
		if (groupSeen.current === groupId) return;
		groupSeen.current = groupId;
		// The old NOC's step and "Register another" organisation must not carry over; the draft is per group.
		void navigate({ search: (prev) => ({ games_id: prev.games_id }), replace: true });
	}, [groupId, navigate]);

	const ready = games.isSuccess && orgs.isSuccess && countries.isSuccess;
	const start = useMemo(() => {
		if (!ready) return null;
		const draft = search.fresh ? null : readLocalDraft(session.user.id, groupId);
		// A new registration is always for the main Games (`is_para` false) that has not ended — there is no
		// Games picker; the Para Games come later. `liveGames` puts the main games first.
		const mains = liveGames(games.data).filter((g) => !g.is_para);
		const isMain = (id: string | undefined) => !!id && mains.some((g) => g.games_id === id);
		const defaultGames = mains[0]?.games_id ?? "";
		if (draft && !search.responsible_organization_id) {
			const restored = { ...EMPTY_FORM, ...draft.form } as RegisterForm;
			if (!isMain(restored.games_id)) {
				restored.games_id = defaultGames;
				restored.sport_id = "";
			}
			return { form: restored, restored: true, step: draft.step };
		}
		const form: RegisterForm = { ...EMPTY_FORM };
		form.games_id = isMain(search.games_id) ? search.games_id! : defaultGames;
		// A scoped login whose writable scope names exactly one organisation starts with it.
		const writable = (session.group?.scopes ?? []).filter((s) => s.can_write);
		const scoped = writable.every((s) => s.responsible_organization) ? [...new Set(writable.map((s) => s.responsible_organization!.id))] : [];
		form.responsible_organization_id = search.responsible_organization_id ?? (scoped.length === 1 ? scoped[0]! : "");
		// Her cascade: a NOC presets the nationality, which picks the identity document.
		const org = orgs.data.find((o) => o.responsible_organization_id === form.responsible_organization_id);
		const country = org?.organization_type?.code === NOC_TYPE_CODE ? countries.data.find((c) => c.country_id === org.country_id) : undefined;
		if (country) {
			form.country_code = country.iso_code;
			form.identification_type = country.iso_code === HOST_COUNTRY_ISO ? "NATIONAL_ID" : "PASSPORT";
		}
		return { form, restored: false, step: "zone" as const };
		// The start is decided once per visit; later search changes are this page's own.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ready, groupId, search.fresh, search.games_id, search.responsible_organization_id]);

	if (!canRegister(session))
		return (
			<div role="alert" className="mx-auto mt-[2rem] grid max-w-[32rem] justify-items-center gap-[0.5rem] rounded-[0.75rem] border border-gms-line bg-base-100 px-[1.25rem] py-[2rem] text-center">
				<h1 className="text-[1.125rem] font-bold text-gms-navy-ink">{t("register.noPermission.title")}</h1>
				<p className="text-[0.84375rem] text-gms-ink-2">{t("register.noPermission.body")}</p>
			</div>
		);
	if (!start) return <div className="skeleton h-[24rem] rounded-[0.75rem]" />;

	return (
		<RegisterFlow
			key={`${groupId ?? ""}${search.fresh ? "f" : ""}${search.games_id ?? ""}${search.responsible_organization_id ?? ""}`}
			me={session}
			mode={{ kind: "new", initial: start.form, restored: start.restored }}
			step={switched ? "zone" : (search.stage ?? start.step)}
			onStep={(s) => void navigate({ search: (prev) => ({ ...prev, stage: s === "personal" ? "personal" : "zone" }) })}
			onCreated={(id) => void navigate({ to: "/accreditation/add/$id", params: { id }, search: { step: "additional", created: true } })}
		/>
	);
}
